/**
 * EditarVeiculoClienteDialog — Ferramenta ÚNICA de edição para o fluxo operacional do Kanban.
 *
 * Filosofia: o sistema tem UMA fonte de verdade para cada entidade.
 *   - Alterar dados aqui → PUT /clients/{id}, PUT /vehicles/{id}, PUT /jobs/{id}
 *   - As alterações refletem em TODOS os módulos (cadastros, kanbans, portal, histórico).
 *
 * Não contém campos de Ordem de Serviço, Orçamento, Peças ou Serviços comerciais.
 * O sistema hoje é uma ferramenta de gestão OPERACIONAL do fluxo do veículo.
 *
 * Recebe `jobId`, busca `GET /jobs/{id}` (que retorna job + vehicle + client) e permite
 * salvar os 3 registros em paralelo.
 */
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/apiClient";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { toast } from "sonner";
import { Save, Car, User as UserIcon, Wrench, Upload, AlertTriangle, UserCog, ExternalLink } from "lucide-react";
import { STATUS_LABELS, STATUSES_BY_TYPE } from "@/components/EditarJobDialog";
import TransferirClienteDialog from "@/components/TransferirClienteDialog";

const PRIORITY_OPTIONS = [
  { value: "normal", label: "Normal" },
  { value: "importante", label: "Importante" },
  { value: "urgente", label: "Urgente" },
  { value: "monitorar", label: "Monitorar" },
];

const FUEL_OPTIONS = ["Gasolina", "Diesel", "Flex", "Elétrico", "Híbrido"];

export default function EditarVeiculoClienteDialog({ jobId, open, onOpenChange, onSaved }) {
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [job, setJob] = useState(null);
  const [vehicle, setVehicle] = useState(null);
  const [client, setClient] = useState(null);
  const [transferOpen, setTransferOpen] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    if (!open || !jobId) {
      setJob(null); setVehicle(null); setClient(null);
      return;
    }
    setLoading(true);
    (async () => {
      try {
        const { data } = await api.get(`/jobs/${jobId}`);
        setJob({
          id: data.job.id,
          technician: data.job.technician || "",
          priority: data.job.priority || "normal",
          notes: data.job.notes || "",
          client_note: data.job.client_note || "",
          status: data.job.status || "entrou",
          job_type: data.job.job_type || "mechanic",
        });
        setVehicle({
          id: data.vehicle.id,
          year: data.vehicle.year || "",
          make: data.vehicle.make || "",
          model: data.vehicle.model || "",
          trim: data.vehicle.trim || "",
          vin: data.vehicle.vin || "",
          plate: data.vehicle.plate || "",
          engine: data.vehicle.engine || "",
          fuel: data.vehicle.fuel || "Gasolina",
          drivetrain: data.vehicle.drivetrain || "",
          mileage: data.vehicle.mileage || "",
          photo_url: data.vehicle.photo_url || "",
        });
        setClient({
          id: data.client.id,
          name: data.client.name || "",
          phone: data.client.phone || "",
          email: data.client.email || "",
          vehicles_count: 0, // preenchido a seguir
        });
        // Conta quantos veículos este cliente possui (ajuda na exibição do warning)
        try {
          const { data: allVehs } = await api.get("/vehicles");
          const count = (allVehs || []).filter((v) => v.client_id === data.client.id).length;
          setClient((c) => (c ? { ...c, vehicles_count: count } : c));
        } catch { /* silencioso */ }
      } catch {
        toast.error("Erro ao carregar dados");
        onOpenChange(false);
      } finally {
        setLoading(false);
      }
    })();
  }, [open, jobId, onOpenChange]);

  const uploadPhoto = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      const token = localStorage.getItem("aab_token");
      const url = `${process.env.REACT_APP_BACKEND_URL}${data.url}${token ? `?auth=${token}` : ""}`;
      setVehicle((v) => ({ ...v, photo_url: url }));
      toast.success("Foto enviada");
    } catch {
      toast.error("Falha no upload");
    } finally {
      setUploading(false);
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!vehicle?.make?.trim() || !vehicle?.model?.trim()) return toast.error("Marca e modelo são obrigatórios");
    setSaving(true);
    try {
      await Promise.all([
        api.put(`/vehicles/${vehicle.id}`, {
          client_id: client.id,
          year: vehicle.year ? parseInt(vehicle.year) : null,
          make: vehicle.make,
          model: vehicle.model,
          trim: vehicle.trim,
          vin: vehicle.vin,
          plate: vehicle.plate,
          engine: vehicle.engine,
          fuel: vehicle.fuel,
          drivetrain: vehicle.drivetrain,
          mileage: vehicle.mileage ? parseInt(vehicle.mileage) : null,
          photo_url: vehicle.photo_url,
        }),
        api.put(`/jobs/${job.id}`, {
          technician: job.technician,
          priority: job.priority,
          notes: job.notes,
          client_note: job.client_note,
          status: job.status,
        }),
      ]);
      toast.success("Veículo e OS atualizados");
      onSaved && onSaved();
      onOpenChange(false);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erro ao salvar");
    } finally {
      setSaving(false);
    }
  };

  const statuses = job ? (STATUSES_BY_TYPE[job.job_type] || STATUSES_BY_TYPE.mechanic) : [];

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-2xl max-h-[92vh] overflow-y-auto" data-testid="edit-veiculo-cliente-dialog">
        <DialogHeader>
          <DialogTitle>Editar dados do atendimento</DialogTitle>
          <DialogDescription>
            Ferramenta única. Alterações aqui atualizam o cadastro real em todo o sistema —
            cliente, veículo, check-in, kanban, portal e histórico ficam sincronizados.
          </DialogDescription>
        </DialogHeader>

        {loading || !job ? (
          <div className="text-sm text-muted-foreground py-10 text-center">Carregando…</div>
        ) : (
          <form onSubmit={submit} className="space-y-4">
            <Tabs defaultValue="veiculo" className="w-full">
              <TabsList className="w-full grid grid-cols-3">
                <TabsTrigger value="veiculo" data-testid="tab-veiculo">
                  <Car className="w-3.5 h-3.5 mr-1.5" /> Veículo
                </TabsTrigger>
                <TabsTrigger value="cliente" data-testid="tab-cliente">
                  <UserIcon className="w-3.5 h-3.5 mr-1.5" /> Cliente
                </TabsTrigger>
                <TabsTrigger value="operacional" data-testid="tab-operacional">
                  <Wrench className="w-3.5 h-3.5 mr-1.5" /> Operacional
                </TabsTrigger>
              </TabsList>

              {/* -------- Veículo -------- */}
              <TabsContent value="veiculo" className="space-y-3 mt-4">
                <div className="grid grid-cols-3 gap-3">
                  <div>
                    <Label>Ano</Label>
                    <Input type="number" value={vehicle.year} onChange={(e) => setVehicle({ ...vehicle, year: e.target.value })} data-testid="edit-veh-year" />
                  </div>
                  <div>
                    <Label>Marca *</Label>
                    <Input value={vehicle.make} onChange={(e) => setVehicle({ ...vehicle, make: e.target.value })} data-testid="edit-veh-make" />
                  </div>
                  <div>
                    <Label>Modelo *</Label>
                    <Input value={vehicle.model} onChange={(e) => setVehicle({ ...vehicle, model: e.target.value })} data-testid="edit-veh-model" />
                  </div>
                </div>
                <div className="grid grid-cols-3 gap-3">
                  <div>
                    <Label>VIN</Label>
                    <Input value={vehicle.vin} onChange={(e) => setVehicle({ ...vehicle, vin: e.target.value.toUpperCase() })} maxLength={17} className="font-mono" data-testid="edit-veh-vin" />
                  </div>
                  <div>
                    <Label>Placa</Label>
                    <Input value={vehicle.plate} onChange={(e) => setVehicle({ ...vehicle, plate: e.target.value.toUpperCase() })} data-testid="edit-veh-plate" />
                  </div>
                  <div>
                    <Label>Milhagem (Mileage)</Label>
                    <Input type="number" value={vehicle.mileage} onChange={(e) => setVehicle({ ...vehicle, mileage: e.target.value })} data-testid="edit-veh-mileage" />
                  </div>
                </div>
                <div className="grid grid-cols-3 gap-3">
                  <div>
                    <Label>Versão</Label>
                    <Input value={vehicle.trim} onChange={(e) => setVehicle({ ...vehicle, trim: e.target.value })} />
                  </div>
                  <div>
                    <Label>Motor</Label>
                    <Input value={vehicle.engine} onChange={(e) => setVehicle({ ...vehicle, engine: e.target.value })} />
                  </div>
                  <div>
                    <Label>Combustível</Label>
                    <Select value={vehicle.fuel} onValueChange={(v) => setVehicle({ ...vehicle, fuel: v })}>
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {FUEL_OPTIONS.map((f) => <SelectItem key={f} value={f}>{f}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <div>
                  <Label>Foto do veículo</Label>
                  <div className="flex gap-2">
                    <Input value={vehicle.photo_url || ""} onChange={(e) => setVehicle({ ...vehicle, photo_url: e.target.value })} placeholder="https://…" />
                    <label className="inline-flex items-center gap-1.5 px-3 h-9 rounded-md border border-input hover:bg-accent cursor-pointer text-sm shrink-0">
                      <Upload className="w-3.5 h-3.5" />
                      {uploading ? "Enviando…" : "Upload"}
                      <input type="file" accept="image/*" className="hidden" onChange={uploadPhoto} />
                    </label>
                  </div>
                  {vehicle.photo_url && (
                    <img src={vehicle.photo_url} alt="preview" className="mt-2 h-28 rounded border border-border object-cover" />
                  )}
                </div>
              </TabsContent>

              {/* -------- Cliente (READ-ONLY + safe actions) -------- */}
              <TabsContent value="cliente" className="space-y-4 mt-4">
                <div className="border border-amber-500/40 bg-amber-500/5 rounded-md p-3 flex items-start gap-2">
                  <AlertTriangle className="w-4 h-4 text-amber-500 shrink-0 mt-0.5" />
                  <div className="text-xs leading-relaxed">
                    <div className="font-semibold text-foreground">Dados do cliente não editáveis aqui.</div>
                    <div className="text-muted-foreground mt-1">
                      Um cliente pode ter <b>vários veículos</b>. Editar o nome ou telefone aqui alteraria os dados em <b>todos</b> os veículos deste cliente.
                      <br />
                      Escolha a ação correta abaixo:
                    </div>
                  </div>
                </div>

                <div className="border border-border rounded-md bg-secondary/30 p-4">
                  <div className="text-[10px] uppercase tracking-widest text-muted-foreground">Cliente atual</div>
                  <div className="font-display text-lg font-bold mt-0.5">{client.name}</div>
                  <div className="text-xs text-muted-foreground mt-0.5">
                    {[client.phone, client.email].filter(Boolean).join(" · ") || "sem contato"}
                  </div>
                  {client.vehicles_count > 1 && (
                    <div className="text-[11px] mt-2 inline-flex items-center gap-1.5 px-2 py-0.5 rounded border border-amber-500/40 text-amber-600 dark:text-amber-500">
                      <AlertTriangle className="w-3 h-3" />
                      Este cliente possui {client.vehicles_count} veículos cadastrados
                    </div>
                  )}
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  <button
                    type="button"
                    onClick={() => { onOpenChange(false); navigate(`/clientes/${client.id}`); }}
                    className="border border-border hover:border-foreground rounded-md p-4 text-left transition-colors"
                    data-testid="goto-client-detail"
                  >
                    <div className="flex items-center gap-2 mb-2">
                      <ExternalLink className="w-4 h-4" />
                      <div className="font-semibold">Editar dados do cliente</div>
                    </div>
                    <div className="text-xs text-muted-foreground">
                      Abre a página do cliente onde você pode renomear, alterar telefone/e-mail. As mudanças afetam TODOS os veículos deste cliente.
                    </div>
                  </button>

                  <button
                    type="button"
                    onClick={() => setTransferOpen(true)}
                    className="border border-foreground hover:bg-secondary rounded-md p-4 text-left transition-colors"
                    data-testid="open-transfer-from-tab"
                  >
                    <div className="flex items-center gap-2 mb-2">
                      <UserCog className="w-4 h-4" />
                      <div className="font-semibold">Transferir este veículo para outro cliente</div>
                    </div>
                    <div className="text-xs text-muted-foreground">
                      Move apenas <b>este veículo</b> (e suas OS/inspeções) para outro cliente. Os outros veículos permanecem com o cliente atual.
                    </div>
                  </button>
                </div>
              </TabsContent>

              {/* -------- Operacional -------- */}
              <TabsContent value="operacional" className="space-y-3 mt-4">
                <div className="text-xs text-muted-foreground bg-secondary/50 rounded-md p-3 leading-relaxed">
                  Estes campos controlam o <b>fluxo operacional</b> do veículo dentro da oficina:
                  status no Kanban, prioridade, técnico responsável e anotações da equipe.
                  Não são campos de orçamento, peças ou ordem de serviço.
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label>Status no Kanban</Label>
                    <Select value={job.status} onValueChange={(v) => setJob({ ...job, status: v })}>
                      <SelectTrigger data-testid="edit-op-status"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {statuses.map((s) => <SelectItem key={s} value={s}>{STATUS_LABELS[s] || s}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label>Prioridade</Label>
                    <Select value={job.priority} onValueChange={(v) => setJob({ ...job, priority: v })}>
                      <SelectTrigger data-testid="edit-op-priority"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {PRIORITY_OPTIONS.map((p) => <SelectItem key={p.value} value={p.value}>{p.label}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <div>
                  <Label>Técnico responsável</Label>
                  <Input value={job.technician} onChange={(e) => setJob({ ...job, technician: e.target.value })} data-testid="edit-op-technician" />
                </div>
                <div>
                  <Label>Anotações <span className="text-[10px] text-muted-foreground font-normal ml-1">(interno — não vai ao cliente)</span></Label>
                  <Textarea rows={3} value={job.notes} onChange={(e) => setJob({ ...job, notes: e.target.value })} placeholder="Ex.: cliente relatou ruído no motor…" data-testid="edit-op-notes" />
                </div>
                <div className="border-t border-border pt-3">
                  <Label className="text-primary">
                    Mensagem ao Cliente
                    <span className="text-[10px] text-muted-foreground font-normal ml-1">(visível no Portal do Cliente)</span>
                  </Label>
                  <Textarea
                    rows={3}
                    value={job.client_note}
                    onChange={(e) => setJob({ ...job, client_note: e.target.value })}
                    placeholder="Ex.: Peça encomendada no dealer, prazo estimado de 5 dias úteis."
                    data-testid="edit-op-client-note"
                    className="border-primary/40 focus-visible:border-primary"
                  />
                  <div className="text-[10px] text-muted-foreground mt-1">
                    Aparece automaticamente no Portal do Cliente, ao lado do status atual. Deixe em branco para não exibir.
                  </div>
                </div>
              </TabsContent>
            </Tabs>

            <DialogFooter className="gap-2 pt-2">
              <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>Cancelar</Button>
              <Button type="submit" disabled={saving} data-testid="edit-vc-save">
                <Save className="w-4 h-4 mr-1.5" />
                {saving ? "Salvando…" : "Salvar veículo e OS"}
              </Button>
            </DialogFooter>
          </form>
        )}
        <TransferirClienteDialog
          vehicle={vehicle}
          currentClient={client ? { id: client.id, name: client.name, phone: client.phone } : null}
          open={transferOpen}
          onOpenChange={setTransferOpen}
          onTransferred={() => {
            onOpenChange(false);
            onSaved && onSaved();
          }}
        />
      </DialogContent>
    </Dialog>
  );
}
